import React from "react";
import { ComponentHelper } from "@magnolia/react-editor";
import { environment } from "../../../environment";

// Renders a campaign-managed slot: the component stores a campaign TAG (plus an optional
// fallback creative), and the campaign-manager module's CampaignReferenceResolver expands
// the tag property into the currently ACTIVE campaign item server-side — date logic
// included. The storefront only renders what arrives (see the resolver rows in the pages
// endpoint decoration, feature-campaign-manager). An unresolved value stays a string:
// no active campaign and no fallback means the slot renders nothing, same as the FTL
// managed-campaign component.
//
// Like Component Factory items, the campaign creative's components belong to another
// workspace and are rendered without edit annotations — they are edited in the Campaign
// Manager app.
// Which campaign is active is decided at request time by the resolver, so the ACTIVE
// creative can be an FTL one even though the choosers guard the fallback — an FTL creative
// cannot render here (its component templates have no SPA mapping). Only headless
// creatives pass; anything else falls through to the fallback, then to nothing.
const isRenderable = (creative) =>
    creative && typeof creative === "object" && creative["mgnl:template"] === environment.CAMPAIGN_FRAGMENT_PAGE_TEMPLATE;

const ManagedCampaign = (props) => {
    if (props.skip) return null;

    const tag = isRenderable(props.tag) ? props.tag : null;
    const fallback = isRenderable(props.fallback) ? props.fallback : null;
    const creative = tag || fallback;

    const main = creative?.main;
    const componentNames = main?.["@nodes"] ?? [];

    const mappings = ComponentHelper.getConfigRef()?.componentMappings;
    const isEditMode = Boolean(ComponentHelper.getMagnoliaContextRef()?.isMagnoliaEdit);

    if (!componentNames.length) {
        const activeButNotHeadless = props.tag && typeof props.tag === "object" && !tag;

        return isEditMode ? (
            <div className="managed-campaign-empty">
                <p>
                    {activeButNotHeadless
                        ? "The active campaign creative for this tag is not a headless creative and cannot render here."
                        : `No active campaign for this tag${fallback ? "" : " and no headless fallback creative selected"}.`}
                </p>
            </div>
        ) : null;
    }

    return (
        <div className="managed-campaign" data-managed-campaign="true" style={{ position: "relative" }}>
            {isEditMode && (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 9999,
                        cursor: "default",
                        background: "rgba(255, 255, 255, 0.01)",
                    }}
                    title="Campaign content is not editable here. Edit the creative in the Campaign Manager app."
                />
            )}
            {componentNames.map((name) => {
                const component = main[name];

                return (
                    <div key={name} className="managed-campaign-item" data-component-template={component?.["mgnl:template"]}>
                        {ComponentHelper.getRenderedComponent(component, mappings)}
                    </div>
                );
            })}
        </div>
    );
};

export default ManagedCampaign;